import React, { useState, useEffect } from 'react';
import useAsyncEffect from 'use-async-effect';
import { Form } from 'react-bootstrap';
import Inputs from 'components/inputs/inputs';
import BackofficeReportsHelper from 'components/navigation/backofficeReports/backofficeReportsHelper';
import _ from 'lodash';

export default function ReportSelection(props) {
  const [configurations, setConfigurations] = useState([]);
  const [selectedConfigurationId, setSelectedConfigurationId] = useState();
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [isAdvancedOptionsError, setIsAdvancedOptionsError] = useState(false);

  const { types, configurationId, onChangeConfigurationId, onChangeReportType, selectedReportTypeId, jobOptions, onChangeJobOptions } = props;

  useAsyncEffect(async () => {
    const configurations = await BackofficeReportsHelper.getReportConfigurations();
    setConfigurations(configurations);
  }, []);

  useEffect(() => {
    const configuration = _.find(configurations, { configurationId: parseInt(configurationId) });
    if (configuration) {
      setSelectedConfigurationId(configuration.configurationId);
    }
  }, [configurationId, configurations]);

  const handleReportTypeChange = (newReportTypeId) => {
    newReportTypeId = parseInt(newReportTypeId);
    const newReportType = _.find(types, { id: newReportTypeId });
    const firstConfiguration = _.find(configurations, { reportTypeId: newReportTypeId });
    if (firstConfiguration) {
      setSelectedConfigurationId(firstConfiguration.configurationId);
      onChangeConfigurationId(firstConfiguration.configurationId);
    }
    onChangeReportType(newReportTypeId, newReportType);
    onChangeJobOptions([]);
  };

  const renderReportTypes = () => {
    const typesGroups = _.groupBy(types, 'group');
    const groups = [];
    for (const groupName in typesGroups) {
      groups.push({ groupName, items: typesGroups[groupName] });
    }

    if (props.reportTypeReadOnly) {
      for (const type of types) {
        if (type.id === selectedReportTypeId) {
          return <Inputs.Text value={type.name} disabled />;
        }
      }
      return <Inputs.Text value={''} disabled />;
    }

    return (
      <Inputs.Select
        required
        placeholder='Please select report type'
        description='Report Type'
        value={selectedReportTypeId}
        onValueChange={handleReportTypeChange}
      >
        {groups.map((group, index) => (
          <Inputs.OptionGroup label={group.groupName} key={`group${index}`}>
            {group.items.map((item, itemIndex) => (
              <Inputs.Option value={item.id} label={item.name} key={`group${index}_item${itemIndex}`} />
            ))}
          </Inputs.OptionGroup>
        ))}
      </Inputs.Select>
    );
  };

  const renderReportConfiguations = () => {
    const filteredConfigurations = configurations.filter((config) => config.reportTypeId === selectedReportTypeId);
    return (
      <Inputs.Select
        required
        placeholder='Please select report view'
        description='Report View'
        value={selectedConfigurationId}
        onValueChange={onChangeConfigurationId}
      >
        {filteredConfigurations.map((config, index) => (
          <Inputs.Option value={config.configurationId} label={config.configName} key={`config${index}`} />
        ))}
      </Inputs.Select>
    );
  };

  return (
    <>
      <Form.Group>
        <Form.Label required>Report Type</Form.Label>
        {renderReportTypes()}
      </Form.Group>
      <Form.Group>
        <Form.Label required>Report View</Form.Label>
        {renderReportConfiguations()}
      </Form.Group>
      <ReportAdvancedOptionsButton
        selectedReportTypeId={selectedReportTypeId}
        open={showAdvancedOptions}
        error={isAdvancedOptionsError}
        onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
      />
      <ReportAdvancedOptions
        show={showAdvancedOptions}
        selectedReportTypeId={selectedReportTypeId}
        jobOptions={jobOptions}
        onChangeJobOptions={onChangeJobOptions}
        setError={setIsAdvancedOptionsError}
      />
      <div className='m-1' style={{ fontSize: '4px' }}>&nbsp;</div>
    </>
  );
}

function ReportAdvancedOptionsButton(props) {
  const show = BackofficeReportsHelper.isAdvancedOptionsAllowed(props.selectedReportTypeId);
  if (!show) {
    return null;
  }

  return (
    <div className='m-2'>
      <span className={'link px-2 py-1' + (props.error ? ' text-danger' : '')} onClick={props.onClick}>
        {props.open ? <i className='icons8-sort-down' /> : <i className='icons8-sort-right-2' />}
        {' '}
        Advanced Options
        {props.error ? <i className='icons8-box-important-2 text-danger ml-2' /> : null}
      </span>
    </div>
  );
}

function ReportAdvancedOptions(props) {
  const jobOptions = props.jobOptions || [];
  const show = props.show && BackofficeReportsHelper.isAdvancedOptionsAllowed(props.selectedReportTypeId);

  const getValue = (section, attribute) => {
    return jobOptions.filter((a) => a.section === section && a.attribute === attribute)?.[0]?.value;
  };

  const setValue = (section, attribute, value) => {
    const newJobOptions = jobOptions.filter((a) => a.section !== section || a.attribute !== attribute);
    newJobOptions.push({ section, attribute, value });
    if (props.onChangeJobOptions) {
      props.onChangeJobOptions(newJobOptions);
    }
  };

  const setValues = (values) => {
    let newJobOptions = [...jobOptions];
    for (let i = 0; i < values.length; i++) {
      newJobOptions = newJobOptions.filter((a) => a.section !== values[i].section || a.attribute !== values[i].attribute);
      newJobOptions.push({ section: values[i].section, attribute: values[i].attribute, value: values[i].value });
    }
    if (props.onChangeJobOptions) {
      props.onChangeJobOptions(newJobOptions);
    }
  };

  const setError = (value) => {
    if (props.setError) {
      props.setError(value);
    }
  }

  switch (props.selectedReportTypeId) {
    case 1:  //Sales - Daily Sales Report
      return <ReportAdvancedOptionsDSR show={show} getValue={getValue} setValue={setValue} setValues={setValues} setError={setError} />;
    case 2:  //Time and Attendance - Labor Distribution Report
      return <ReportAdvancedOptionsLDR show={show} getValue={getValue} setValue={setValue} setValues={setValues} setError={setError} />;
    case 28: //Time and Attendance - Payroll Report
      return <ReportAdvancedOptionsPAYROLL show={show} getValue={getValue} setValue={setValue} setValues={setValues} setError={setError} />;
    default: return null;
  }
}

function ReportAdvancedOptionsLDR(props) {
  if (!props.show) {
    return null;
  }

  return (
    <div className='card card-body card-light my-2 mx-2'>
      <Inputs.Checkbox
        className='mb-0'
        label='Show Wage &amp; Tip Information'
        block
        checked={props.getValue('REPORT_PARAMETERS', 'no_pay_rate') === '1'}
        onValueChange={(v) => props.setValue('REPORT_PARAMETERS', 'no_pay_rate', v ? '1' : '0')}
      />
    </div>
  );
}

function ReportAdvancedOptionsPAYROLL(props) {
  if (!props.show) {
    return null;
  }

  return (
    <div className='card card-body card-light my-2 mx-2'>
      <Inputs.Checkbox
        className='mb-0'
        label='Show Wage &amp; Tip Information'
        block
        checked={props.getValue('REPORT_PARAMETERS', 'rateofpay_incl') === '1'}
        onValueChange={(v) => props.setValue('REPORT_PARAMETERS', 'rateofpay_incl', v ? '1' : '0')}
      />
    </div>
  );
}

function ReportAdvancedOptionsDSR(props) {
  const getCountColumnsValue = () => {
    const trans = props.getValue('LAYOUT', 'show_trans_count') === '1';
    const cust = props.getValue('LAYOUT', 'show_cust_count') === '1';
    const cover = props.getValue('LAYOUT', 'show_cover_count') === '1';
    if (trans && cover) {
      return '1';
    }
    if (cust && cover) {
      return '2';
    }
    return '0';
  };

  const setCountColumnsValue = (v) => {
    switch (v) {
      case '0':
        props.setValues([
          {
            section: 'LAYOUT',
            attribute: 'show_trans_count',
            value: '1',
          },
          {
            section: 'LAYOUT',
            attribute: 'show_cust_count',
            value: '1',
          },
          {
            section: 'LAYOUT',
            attribute: 'show_cover_count',
            value: '0',
          },
          {
            section: 'LAYOUT',
            attribute: 'show_count_1',
            value: '1',
          },
          {
            section: 'LAYOUT',
            attribute: 'show_count_2',
            value: '2',
          },
        ]);
        break;
      case '1':
        props.setValues([
          {
            section: 'LAYOUT',
            attribute: 'show_trans_count',
            value: '1',
          },
          {
            section: 'LAYOUT',
            attribute: 'show_cust_count',
            value: '0',
          },
          {
            section: 'LAYOUT',
            attribute: 'show_cover_count',
            value: '1',
          },
          {
            section: 'LAYOUT',
            attribute: 'show_count_1',
            value: '1',
          },
          {
            section: 'LAYOUT',
            attribute: 'show_count_2',
            value: '3',
          },
        ]);
        break;
      case '2':
        props.setValues([
          {
            section: 'LAYOUT',
            attribute: 'show_trans_count',
            value: '0',
          },
          {
            section: 'LAYOUT',
            attribute: 'show_cust_count',
            value: '1',
          },
          {
            section: 'LAYOUT',
            attribute: 'show_cover_count',
            value: '1',
          },
          {
            section: 'LAYOUT',
            attribute: 'show_count_1',
            value: '2',
          },
          {
            section: 'LAYOUT',
            attribute: 'show_count_2',
            value: '3',
          },
        ]);
        break;
      default:
        break;
    }
  };

  const setTaxDetailValue = (v) => {
    switch (v) {
      case '0':
        props.setValues([
          {
            section: 'REPORT_PARAMETERS',
            attribute: 'detail_inc',
            value: '0',
          },
          {
            section: 'LAYOUT',
            attribute: 'tax_detail',
            value: '0',
          },
        ]);
        break;
      case '1':
        props.setValues([
          {
            section: 'REPORT_PARAMETERS',
            attribute: 'detail_inc',
            value: '1',
          },
          {
            section: 'LAYOUT',
            attribute: 'tax_detail',
            value: '1',
          },
        ]);
        break;
      default:
        break;
    }
  };

  return (
    <div className={'card card-body card-light my-2 mx-2' + (props.show ? '' : ' d-none')}>
      <div className='row'>
        <div className='col-12 col-md-6 mt-2'>
          <div className='mb-2'><div className='form-label'>Layout</div></div>
          <Inputs.RadioGroup value={props.getValue('LAYOUT', 'sales_view') || '0'} onValueChange={(v) => props.setValue('LAYOUT', 'sales_view', v)}>
            <Inputs.Radio label='Prior Year Comparison' value='0' block />
            <Inputs.Radio label='Gross/Net Discount' value='1' block />
          </Inputs.RadioGroup>
        </div>
        <div className='col-12 col-md-6 mt-2'>
          <div className='mb-2'><strong>Misc. Items or Tax Details</strong></div>
          <Inputs.RadioGroup value={props.getValue('LAYOUT', 'tax_detail') || '0'} onValueChange={setTaxDetailValue}>
            <Inputs.Radio label='Misc. Items with Tax Summary' value='0' block />
            <Inputs.Radio label='Tax Details Only' value='1' block />
          </Inputs.RadioGroup>
        </div>
        <div className='col-12 col-md-6 mt-2'>
          <div className='mb-2'><strong>Count Columns</strong></div>
          <Inputs.RadioGroup value={getCountColumnsValue()} onValueChange={setCountColumnsValue}>
            <Inputs.Radio label='Transaction & Customer Count' value='0' block />
            <Inputs.Radio label='Transaction & Cover Count' value='1' block />
            <Inputs.Radio label='Customer & Cover Count' value='2' block />
          </Inputs.RadioGroup>
        </div>
        <div className='col-12 col-md-6 mt-2'>
          <div className='mb-2'><strong>Average Column</strong></div>
          <Inputs.RadioGroup value={props.getValue('LAYOUT', 'average_sales_opts') || '0'} onValueChange={(v) => props.setValue('LAYOUT', 'average_sales_opts', v)}>
            <Inputs.Radio label='Average Transaction' value='0' block />
            <Inputs.Radio label='Average Customer' value='1' block />
            <Inputs.Radio label='Average Cover' value='2' block />
          </Inputs.RadioGroup>
        </div>
      </div>
    </div>
  );
}

/*function ReportAdvancedOptionsDSR(props) {
  const [isDefaultCountValues, setIsDefaultCountValues] = useState(true);
  const [countColsSelected, setCountColsSelected] = useState(2);
  const isCountInvalid = countColsSelected !== 2;

  props.setError(isCountInvalid);

  const setCountColumn = (attribute, bool) => {
    const value = bool ? '1' : '0';
    switch (attribute) {
      case 'show_trans_count':
        if (isDefaultCountValues) {
          props.setValues([
            { section: 'LAYOUT', attribute: 'show_trans_count', value: '0' },
            { section: 'LAYOUT', attribute: 'show_cust_count', value: '1' },
          ]);
        }
        else {
          props.setValue('LAYOUT', 'show_trans_count', value);
        }
        break;
      case 'show_cust_count':
        if (isDefaultCountValues) {
          props.setValues([
            { section: 'LAYOUT', attribute: 'show_trans_count', value: '1' },
            { section: 'LAYOUT', attribute: 'show_cust_count', value: '0' },
          ]);
        }
        else {
          props.setValue('LAYOUT', 'show_cust_count', value);
        }
        break;
      case 'show_cover_count':
        if (isDefaultCountValues) {
          props.setValues([
            { section: 'LAYOUT', attribute: 'show_trans_count', value: '1' },
            { section: 'LAYOUT', attribute: 'show_cust_count', value: '1' },
            { section: 'LAYOUT', attribute: 'show_cover_count', value: value },
          ]);
        }
        else {
          props.setValue('LAYOUT', 'show_cover_count', value);
        }
        break;
      default:
        break;
    }
    setCountColsSelected(countColsSelected + (bool ? 1 : -1));
    setIsDefaultCountValues(false);
  };

  return (
    <div className={'card card-body card-light my-2 mx-2' + (props.show ? '' : ' d-none')}>
      <div className='row'>
        <div className='col-12 col-sm-6'>
          <div className='mb-2'><div className='form-label'>Count Columns</div> <span className='status ml-2'>(Choose 2)</span></div>
          <Inputs.Checkbox
            label='Transaction Count'
            block
            isInvalid={isCountInvalid}
            checked={isDefaultCountValues || props.getValue('LAYOUT', 'show_trans_count') === '1'}
            onValueChange={(v) => setCountColumn('show_trans_count', v)}
          />
          <Inputs.Checkbox
            label='Customer Count'
            block
            isInvalid={isCountInvalid}
            checked={isDefaultCountValues || props.getValue('LAYOUT', 'show_cust_count') === '1'}
            onValueChange={(v) => setCountColumn('show_cust_count', v)}
          />
          <Inputs.Checkbox
            label='Cover Count'
            block
            isInvalid={isCountInvalid}
            checked={props.getValue('LAYOUT', 'show_cover_count') === '1'}
            onValueChange={(v) => setCountColumn('show_cover_count', v)}
          />
          {isCountInvalid && <div className='invalid-feedback d-block mb-2 mx-0'>Exactly 2 options must be selected</div>}
        </div>
        <div className='col-12 col-sm-6 mt-2'>
          <div className='mb-2'><div className='form-label'>Average Column</div></div>
          <Inputs.RadioGroup value={props.getValue('LAYOUT', 'average_sales_opts')}>
            <Inputs.Radio label='Average Transaction' value='1' block />
            <Inputs.Radio label='Average Customer' value='2' block />
            <Inputs.Radio label='Average Cover' value='3' block />
          </Inputs.RadioGroup>
        </div>
        <div className='col-12 col-sm-6 mt-2'>
          <div className='mb-2'><div className='form-label'>Tax Section</div></div>
          <Inputs.RadioGroup value={0}>
            <Inputs.Radio label='Tax Summary and Misc. Items' value={0} block />
            <Inputs.Radio label='Tax Detail' value={1} block />
          </Inputs.RadioGroup>
        </div>
        <div className='col-12 col-sm-6 mt-2'>
          <div className='mb-2'><div className='form-label'>Layout</div></div>
          <Inputs.RadioGroup value={0}>
            <Inputs.Radio label='Prior Year Comparison' value={0} block />
            <Inputs.Radio label='Gross/Net Discount' value={1} block />
          </Inputs.RadioGroup>
        </div>
      </div>
    </div>
  );
}*/